"use client";

import { Alert, Table } from "@mantine/core";
import { FiBell, FiCheck, FiInfo } from "react-icons/fi";

import {
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";

type PenaltyRuleChangeModalProps = {
  opened: boolean;
  onConfirm: () => void;
};

const tableStyles = {
  table: {
    borderRadius: "var(--mantine-radius-md)",
    overflow: "hidden" as const,
    border: "1px solid var(--mantine-color-gray-3)",
  },
  thead: {
    tr: {
      backgroundColor: "var(--mantine-color-orange-0)",
    },
  },
  th: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--mantine-color-dark-6)",
    padding: "10px 12px",
  },
  td: {
    fontSize: 13,
    color: "var(--mantine-color-dark-7)",
    padding: 12,
    verticalAlign: "middle" as const,
  },
};

export function PenaltyRuleChangeModal({
  opened,
  onConfirm,
}: PenaltyRuleChangeModalProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal
      opened={opened}
      close={handleConfirm}
      centered
      size="md"
      padding="xl"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Badge
            size="sm"
            variant="light"
            color="orange"
            radius="xl"
            fw={600}
            w="fit-content"
            leftSection={<FiBell size={14} aria-hidden />}
            styles={{
              root: {
                paddingLeft: 10,
                paddingRight: 10,
                textTransform: "none",
              },
            }}
          >
            ルール変更のお知らせ
          </Badge>
          <Text size="xl" fw={700} lh={1.4} c="dark.7">
            遅刻・早退ペナルティが変わりました
          </Text>
        </Stack>

        <Table withTableBorder={false} styles={tableStyles}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>条件</Table.Th>
              <Table.Th w={100} ta="center">
                ペナルティ
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>
                <Text size="sm">遅刻・早退</Text>
                <Text fz="xs" c="gray.7" mt={2}>
                  合計1時間以下
                </Text>
              </Table.Td>
              <Table.Td ta="center">
                <Badge
                  size="sm"
                  variant="light"
                  color="orange"
                  radius="xl"
                  fw={700}
                  styles={{ root: { textTransform: "none" } }}
                >
                  -3pt
                </Badge>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text size="sm">遅刻・早退</Text>
                <Text fz="xs" c="gray.7" mt={2}>
                  合計1時間超
                </Text>
              </Table.Td>
              <Table.Td ta="center">
                <Badge
                  size="sm"
                  variant="light"
                  color="red"
                  radius="xl"
                  fw={700}
                  styles={{ root: { textTransform: "none" } }}
                >
                  -5pt
                </Badge>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>

        <Alert
          icon={<FiInfo size={16} aria-hidden />}
          color="blue"
          variant="light"
          radius="md"
          styles={{
            message: {
              fontSize: 13,
              lineHeight: 1.6,
              color: "var(--mantine-color-blue-7)",
            },
          }}
        >
          遅刻・早退の合計が1時間を超えた場合、ペナルティが大きくなります。
        </Alert>

        <Button
          variant="outline"
          size="lg"
          color="gray"
          fullWidth
          onClick={handleConfirm}
          styles={{
            root: {
              height: 48,
              fontSize: 15,
              fontWeight: 600,
              color: "var(--mantine-color-dark-7)",
              borderColor: "var(--mantine-color-gray-4)",
            },
          }}
        >
          <Group gap="xs" justify="center">
            <FiCheck size={18} aria-hidden />
            内容を確認しました
          </Group>
        </Button>
      </Stack>
    </Modal>
  );
}
